import { OrganizationCodes } from '@revenuemarket/bank';
import { Dayjs } from 'dayjs';

import { API } from '~apis/axios/instance';
import {
  CancelType,
  HomeForeignType,
  PaymentType,
  RangeType as BillingItemRangeType,
  UnifiedCardTransactionRangeType,
  CardProjectionType,
} from '~apis/card';
import { AccountTransactionSearchParamFieldType } from '~apis/cash-flow-overview/transactions/AccountTransactionSearchParamFieldType';
import {
  FilterOperation,
  MappingLabelType,
} from '~apis/cash-flow-overview/transactions/getTransactions';
import { CashReceiptTransactionType, DeductionType } from '~apis/cash-receipt';
import { SettlementStatus } from '~components/Settlement/utils';
import { TaxationType } from '~components/TaxInvoice/type';
import { CashFlowType } from '~models/cashFlow';
import { ModificationType } from '~models/tax-invoice';

export type DownloadType = 'EXCEL' | 'PDF';

export type DownloadDataType =
  | 'BANK_ACCOUNTS'
  | 'CASH_FLOW'
  | 'SALES_INVOICE_MAPPING'
  | 'PURCHASE_INVOICE_MAPPING'
  | 'BANK_TRANSACTION_LABELS'
  | 'CARD_BILLING_ITEM_LABELS'
  | 'UNIFIED_CARD_TRANSACTION_LABELS'
  | 'BANK_TRANSACTION_DATA'
  | 'BANK_TRANSACTION_DATA_WEHAGO'
  | 'CARD_APPROVAL_TRANSACTION_DATA'
  | 'CARD_PURCHASE_TRANSACTION_DATA'
  | 'CARD_PURCHASE_TRANSACTION_DATA_WEHAGO'
  | 'TAX_INVOICE_DATA'
  | 'CASH_RECEIPT_DATA';

export const getExportFile = ({
  companyId,
  startDate,
  endDate,
  dataType,
  fileType,
  fxRuleType,
  billingItemRangeType,
  rangeType,
  exportFormat,
}: {
  companyId: string;
  startDate: Dayjs;
  endDate: Dayjs;
  dataType: DownloadDataType;
  fileType: 'EXCEL' | 'ZIP';
  fxRuleType?: 'TRANSACTION_DATE' | 'END_OF_PERIOD';
  billingItemRangeType?: 'PAYMENT' | 'USED_DATE' | 'TRANSACTION_DATE';
  rangeType?: 'DATE' | 'WEEK' | 'MONTH' | 'QUARTER' | 'YEAR';
  exportFormat?: 'BASIC' | 'WEHAGO';
}) => {
  return API.Instance.get(`/api/v3/companies/${companyId}/export`, {
    responseType: 'blob',
    params: {
      companyId,
      startDate: startDate.format('YYYY-MM-DD'),
      endDate: endDate.format('YYYY-MM-DD'),
      dataType,
      fileType,
      fxRuleType,
      billingItemRangeType,
      rangeType,
      exportFormat,
    },
  });
};

export interface BankAccountExcelExportOptions {
  searchParamFilter?: string;
  searchParamFieldTypesFilter?: AccountTransactionSearchParamFieldType[];
  cashFlowDirectionFilter?: CashFlowType;
  accountsFilter?: number[];
  /** ex. ['급여', '이체'] */
  transactionTypesFilter?: string[];
}

export interface CardExcelExportOptions {
  homeForeignTypeFilter?: HomeForeignType[];
  cancelTypesFilter?: CancelType[];
  paymentTypesFilter?: PaymentType[];
  cardNosFilter?: string[];
  searchParamFilter?: string;
}

export enum FxRuleType {
  TRANSACTION_DATE = 'TRANSACTION_DATE',
  END_OF_PERIOD = 'END_OF_PERIOD',
}
export enum CardSalesTimeUnit {
  DAILY = 'DAILY',
  MONTHLY = 'MONTHLY',
  YEARLY = 'YEARLY',
  TOTAL = 'TOTAL',
}
export enum CardSalesAggregationDateType {
  APPROVAL = 'APPROVAL', // 승인일
  PURCHASE = 'PURCHASE', // 매입일
}

export interface CardSalesExcelExportOptions {
  timeUnit?: CardSalesTimeUnit;
  dateType?: CardSalesAggregationDateType;
}

export enum TaxInvoiceExportFilterType {
  SALES = 'SALES',
  PURCHASE = 'PURCHASE',
}

export interface TaxInvoiceExcelExportOptions {
  typeFilter?: TaxInvoiceExportFilterType;
  searchParamFilter?: string;
  bizRegNoFilter?: string;
  taxationTypeFilter?: TaxationType[];
  modificationTypeFilter?: ModificationType[];
}

export interface CashReceiptExcelExportOptions {
  supplierCompanyNameFilter?: string[];
  deductTypesFilter?: DeductionType[];
  transactionTypesFilter?: CashReceiptTransactionType[];
  searchParamFilter?: string;
}

export interface BankTransactionLabelsExcelExportOptions {
  searchParamFilter?: string;
  searchParamFieldTypesFilter?: AccountTransactionSearchParamFieldType[];
  transactionCashFlowDirectionFilter?: CashFlowType;
  accountsFilter?: number[];
  businessEntityIdsFilter?: number[];
  groupLabelIdsFilter?: number[];
  categoryLabelKeysFilter?: string[];
  onlyUnclassifiedFilter?: boolean;
  mappingLabelFilter?: MappingLabelType;
  mappingLabelTypesFilter?: MappingLabelType[];
  accountFilterOp?: FilterOperation;
  categoryLabelFilterOp?: FilterOperation;
  businessEntityIdFilterOp?: FilterOperation;
  groupLabelFilterOp?: FilterOperation;
  mappingLabelTypesFilterOp?: FilterOperation;
  onlyUnmappedFilter?: boolean;
  /** 백엔드에서 각 거래내역별로 입/출금 두가지 라벨로 분류했을때 그것에 대한 필터 하는 조건. 25/09/12 기준 프론트는 사용하지 않음. */
  // labelCashFlowDirectionFilter?: CashFlowType;
}

export interface CardLabelsExcelExportOptions {
  searchParamFilter?: string;
  categoryLabelKeysFilter?: string[];
  categoryLabelFilterOp?: FilterOperation;
  businessEntityIdsFilter?: number[];
  businessEntityIdFilterOp?: FilterOperation;
  customLabelIdsFilter?: number[];
  customLabelFilterOp?: FilterOperation;
  organizationCodesFilter?: OrganizationCodes[];
  organizationCodeFilterOp?: FilterOperation;
  onlyUnclassifiedFilter?: boolean;
  excludePrePaidDuplicateFilter?: boolean;
  cardNosFilter?: string[];
  cardNoFilterOp?: FilterOperation;
}

export interface UnifiedCardTransactionLabelsExcelExportOptions {
  rangeTypeFilter?: UnifiedCardTransactionRangeType;
  organizationCodesFilter?: OrganizationCodes[];
  organizationCodeFilterOp?: FilterOperation;
  cardNosFilter?: string[];
  cancelTypesFilter?: CancelType[];
  paymentTypesFilter?: PaymentType[];
  homeForeignTypesFilter?: HomeForeignType[];
  searchParamFilter?: string;
  categoryLabelKeysFilter?: string[];
  categoryLabelFilterOp?: FilterOperation;
  businessEntityIdsFilter?: number[];
  businessEntityIdFilterOp?: FilterOperation;
  groupLabelIdsFilter?: number[];
  groupLabelFilterOp?: FilterOperation;
}

export interface SalesInvoiceMappingExcelExportOptions {
  businessEntityIdsFilter?: number[];
  searchParamFilter?: string;
  statusFilter?: SettlementStatus;
}

export interface PurchaseInvoiceMappingExcelExportOptions {
  businessEntityIdsFilter?: number[];
  searchParamFilter?: string;
  statusFilter?: SettlementStatus;
}

export interface DownloadFileByPostRequest {
  companyId: string;
  dataType: DownloadDataType;
  fileType: 'EXCEL' | 'ZIP';
  startDate: Dayjs;
  endDate: Dayjs;
  rangeType: 'DATE' | 'WEEK' | 'MONTH' | 'QUARTER' | 'YEAR';
  /** Ex) transactionAt,asc */
  sort?: string;
  bankAccountExportOptions?: {
    fxRuleTypeFilter: FxRuleType;
  };
  cashFlowExportOptions?: {
    fxRuleTypeFilter: FxRuleType;
    cardProjectionType: CardProjectionType;
  };
  cardBillingItemLabelsOptions?: {
    billingItemRangeTypeFilter: BillingItemRangeType;
  };
  // 은행 거래내역
  bankAccountTransactionOptions?: BankAccountExcelExportOptions;
  // 카드 이용내역
  cardApprovalOptions?: CardExcelExportOptions;
  cardPurchaseOptions?: CardExcelExportOptions;
  // 세금계산서
  taxInvoiceOptions?: TaxInvoiceExcelExportOptions;
  // 현금영수증
  cashReceiptOptions?: CashReceiptExcelExportOptions;
  // 은행 거래내역 분류
  bankTransactionLabelsOptions?: BankTransactionLabelsExcelExportOptions;
  // 카드 청구내역 분류
  cardLabelsOptions?: CardLabelsExcelExportOptions;
  // 카드 이용내역 분류
  unifiedCardTransactionLabelsOptions?: UnifiedCardTransactionLabelsExcelExportOptions;
  // 매출 정산
  salesInvoiceMappingOptions?: SalesInvoiceMappingExcelExportOptions;
  // 매입 정산
  purchaseInvoiceMappingOptions?: PurchaseInvoiceMappingExcelExportOptions;

  // 파트너스 서비스 옵션.
  cardSalesOptions?: CardSalesExcelExportOptions;
}

export const downloadFileByPost = ({
  companyId,
  startDate,
  endDate,
  ...data
}: DownloadFileByPostRequest) => {
  return API.Instance.post(
    `/api/v3/companies/${companyId}/export/excel`,
    {
      ...data,
      startDate: startDate.format('YYYY-MM-DD'),
      endDate: endDate.format('YYYY-MM-DD'),
    },
    { responseType: 'blob' },
  );
};
